import {
  ComponentProps,
  InputHTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  isValidElement,
  useState,
} from 'react'
import TooltipLabel from './TooltipLabel'
import { Input } from '../../../ui/input'
import { Label } from '../../../ui/label'
import { classes, throwUnsupportedValue } from 'src/utils'
import {
  LinkIcon,
  LockClosedIcon,
  LockOpenIcon,
  MailIcon,
  PhoneIcon,
  SearchIcon,
} from '@heroicons/react/solid'

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value'> & {
  label: string
  hideLabel?: boolean
  className?: string
  tooltip?: ReactNode
} & (
    | {
        value: string
        setValue: (value: string) => void
        type?: 'text' | 'password' | 'email' | 'url' | 'search' | 'tel'
      }
    | {
        value: number
        setValue: (value: number) => void
        type: 'number'
      }
  )

const InputField = ({
  type,
  label,
  hideLabel,
  className,
  tooltip,
  setValue,
  value,
  required = true,
  max,
  min,
  ...props
}: Props) => {
  const [hidePassword, setHidePassword] = useState(true)
  const icon = getInputIcon(type || 'text', hidePassword)

  return (
    <div className="flex flex-col items-start gap-1 group w-full">
      {!hideLabel && (
        <div className="mb-1">
          {tooltip === undefined ? (
            <Label htmlFor={label}>{label}</Label>
          ) : (
            <TooltipLabel label={label} tooltip={tooltip} />
          )}
        </div>
      )}

      <Input
        icon={
          isValidElement(icon) &&
          cloneElement(icon, {
            'aria-hidden': true,
            className: classes(
              'box-content p-2 absolute top-0 bottom-0 h-5 text-primary-foreground',
              (type === 'password' || type === 'search') && 'cursor-pointer',
            ),
            onClick: () => {
              if (type === 'password') {
                setHidePassword((prev) => !prev)
              }
            },
          })
        }
        className={classes(className, icon ? 'pl-10 pr-3' : '')}
        id={label}
        onChange={(e) => {
          if (type === 'number') {
            if (max !== undefined) {
              if (Number(e.target.value) > Number(max)) {
                setValue(Number(max))
                return
              }

              if (Number(e.target.value) < Number(min)) {
                setValue(Number(min))
                return
              }

              setValue(Number(e.target.value))
            }
          } else {
            setValue(e.target.value)
          }
        }}
        value={value}
        type={
          type === 'password'
            ? hidePassword
              ? 'password'
              : 'text'
            : type || 'text'
        }
        required={required}
        {...props}
      />
    </div>
  )
}

function getInputIcon(
  type: Exclude<Props['type'], undefined>,
  hidePassword?: boolean,
): ReactElement<ComponentProps<'svg'>> | null {
  switch (type) {
    case 'search':
      return <SearchIcon />
    case 'tel':
      return <PhoneIcon />
    case 'email':
      return <MailIcon />
    case 'password':
      return hidePassword ? <LockClosedIcon /> : <LockOpenIcon />
    case 'url':
      return <LinkIcon />
    case 'number':
    case 'text':
      return null
    default:
      return throwUnsupportedValue(type)
  }
}

export default InputField
